import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  logTrackingPageView,
  sendTrackingHeartbeat,
  startTrackingSession,
} from '../api/services/trackingService';

const SESSION_ID_KEY = 'edenTrackingSessionId';
const HEARTBEAT_INTERVAL_MS = 30000;

function createSessionId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = createSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

function getCurrentFullUrl() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function sendPageView(pageView) {
  return logTrackingPageView(pageView).catch((err) => {
    console.error('Failed to log page view:', err);
  });
}

function sendKeepalive(path, payload) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token || !baseUrl) return;

  fetch(`${baseUrl}${path}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-api-key': process.env.REACT_APP_API_KEY || '',
      'x-computer-name': navigator.platform,
    },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

export default function useCustomerSessionTracking() {
  const location = useLocation();
  const userInfo = useSelector((state) => state.userInfo);
  const currentPageRef = useRef(null);
  const previousUrlRef = useRef(document.referrer || null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || userInfo.userId === 0) return;

    const sessionId = getSessionId();
    const sessionPayload = {
      sessionId,
      idCustomer: userInfo.idCustomer,
      landingPage: getCurrentFullUrl(),
      referrer: document.referrer || null,
    };

    const startSession = () => {
      return startTrackingSession(sessionPayload).catch((err) => {
        console.error('Failed to start tracking session:', err);
      });
    };

    const heartbeat = () => {
      return sendTrackingHeartbeat(sessionId)
        .then((result) => {
          if (result?.data?.affected === 0) {
            return startSession();
          }
          return result;
        })
        .catch((err) => {
          console.error('Failed to send tracking heartbeat:', err);
        });
    };

    startSession().then(() => {
      heartbeat();
    });

    const intervalId = window.setInterval(() => {
      heartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    const handlePageHide = () => {
      const page = currentPageRef.current;
      if (page) {
        const leftAt = new Date();
        sendKeepalive('/tracking/page-view', {
          ...page,
          leftAt: leftAt.toISOString(),
          durationSec: Math.max(
            0,
            Math.round((leftAt.getTime() - page.enteredTime) / 1000),
          ),
        });
      }

      sendKeepalive('/tracking/session/end', { sessionId });
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [userInfo.idCustomer, userInfo.userId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || userInfo.userId === 0) return;

    const sessionId = getSessionId();
    const now = new Date();
    const currentFullUrl = getCurrentFullUrl();

    if (currentPageRef.current) {
      const previousPage = currentPageRef.current;
      const durationSec = Math.max(
        0,
        Math.round((now.getTime() - previousPage.enteredTime) / 1000),
      );

      sendPageView({
        ...previousPage,
        leftAt: now.toISOString(),
        durationSec,
      });
    }

    currentPageRef.current = {
      sessionId,
      path: location.pathname,
      fullUrl: currentFullUrl,
      title: document.title || null,
      referrer: previousUrlRef.current,
      enteredAt: now.toISOString(),
      enteredTime: now.getTime(),
      leftAt: null,
      durationSec: null,
    };

    previousUrlRef.current = currentFullUrl;
  }, [location.pathname, location.search, location.hash, userInfo.userId]);
}
